<template>
  <div class="dashboard-container">
    <!-- 顶部概览标头 -->
    <div class="dashboard-header m-surface">
      <div class="header-left">
        <div class="header-eyebrow">
          <span class="live-pulse"></span>
          <span>系统在线 · 实时运营雷达</span>
        </div>
        <h1 class="header-title">企业业务全景工作台</h1>
      </div>
      <div class="header-right">
        <el-button
          class="refresh-btn"
          size="small"
          :loading="loading"
          @click="loadAllData"
        >
          <i class="iconfont icon-tubiao-27 mr-5"></i> 刷新指标
        </el-button>
        <span class="header-date">{{ currentDate }}</span>
      </div>
    </div>

    <!-- 四维业务雷达大卡 (Executive Metric Cards) -->
    <div class="metric-grid">
      <MetricCard
        label="本年度订单总额"
        :value="radarData.orderTotalAmount"
        prefix="¥"
        :decimals="2"
        :clickable="true"
        :hint="radarData.orderHint"
        @click="goToRoute('/order/Index')"
      />

      <MetricCard
        label="待审订单单据"
        :value="radarData.pendingApprovals"
        unit="件"
        :clickable="true"
        hint="点击直达订单审批中心处理待办"
        @click="goToRoute('/approval/approvalOrder/Index')"
      />

      <MetricCard
        label="流转中业务工单"
        :value="radarData.inProgressBusiness"
        unit="单"
        :clickable="true"
        :hint="radarData.businessHint"
        @click="goToRoute('/business/businessList/Index')"
      />

      <MetricCard
        label="流转中待收款项"
        :value="radarData.pendingReceived"
        unit="笔"
        :clickable="true"
        hint="点击直达待收款列表处理财务"
        @click="goToRoute('/financial/Index')"
      />
    </div>

    <!-- 中部工作区：高频入口 + 快捷动态 -->
    <div class="dashboard-body">
      <!-- 左侧：快捷操作盘 -->
      <div class="body-panel m-surface">
        <div class="panel-head">
          <span class="panel-title">高频业务快捷通道</span>
          <span class="panel-sub">常用作业一键直达</span>
        </div>
        <div class="action-grid">
          <div
            v-for="act in quickActions"
            :key="act.title"
            class="action-card m-groove"
            @click="goToRoute(act.path)"
          >
            <div class="action-icon" :style="{ color: act.color }">
              <span class="iconfont" :class="act.icon"></span>
            </div>
            <div class="action-info">
              <span class="action-title">{{ act.title }}</span>
              <span class="action-desc">{{ act.desc }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：重点业务动态与通知 -->
      <div class="body-panel m-surface">
        <div class="panel-head">
          <span class="panel-title">流转预警与操作审计</span>
          <span class="panel-sub">近 24 小时动态</span>
        </div>
        <div class="notice-list" v-loading="noticeLoading">
          <div
            v-for="item in noticeList"
            :key="item.id"
            class="notice-item"
            :class="{ 'is-clickable': !!item.targetUrl }"
            @click="handleNoticeClick(item)"
          >
            <span class="notice-dot" :class="item.type"></span>
            <div class="notice-content">
              <span class="notice-title" :title="item.title">{{ item.title }}</span>
              <span class="notice-time">{{ item.time }}</span>
            </div>
            <span class="notice-tag">{{ item.tag }}</span>
          </div>
          <div v-if="!noticeLoading && noticeList.length === 0" class="notice-empty">
            暂无最新流转预警与操作动态
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import MetricCard from '@/components/dashboard/MetricCard.vue'
import { getPageList } from '@/apis/Dictionary'
import { getNotificationPage } from '@/apis/notification'
import { userOperationLogList } from '@/apis/UserLogs'

export default defineComponent({
  name: 'Dashboard',
  components: {
    MetricCard
  },
  setup() {
    const router = useRouter()
    const currentDate = ref('')
    const loading = ref(false)
    const noticeLoading = ref(false)

    const radarData = reactive({
      orderTotalAmount: 0,
      orderCount: 0,
      orderHint: '正在统计订单数据...',
      pendingApprovals: 0,
      inProgressBusiness: 0,
      totalBusiness: 0,
      businessHint: '正在统计流转业务...',
      pendingReceived: 0
    })

    const quickActions = [
      {
        title: '综合业务登记',
        desc: '查看全生命周期业务流转与看板',
        path: '/business/businessList/Index',
        icon: 'icon-tubiao-08',
        color: 'var(--blue)'
      },
      {
        title: '订单全生命周期',
        desc: '查看与维护客户订单明细',
        path: '/order/Index',
        icon: 'icon-tubiao-14',
        color: '#7c3aed'
      },
      {
        title: '客户关系档案',
        desc: '客户公司与联系人资料库',
        path: '/customer/customerInfo/Index',
        icon: 'icon-tubiao-07',
        color: 'var(--leaf-text)'
      },
      {
        title: '设备报修与工单',
        desc: '工程师派工与检修流程',
        path: '/repair/Index',
        icon: 'icon-tubiao-09',
        color: '#f59e0b'
      }
    ]

    const noticeList = ref<Array<{
      id: number | string;
      title: string;
      time: string;
      tag: string;
      type: string;
      targetUrl?: string;
    }>>([])

    function formatDate() {
      const now = new Date()
      currentDate.value = `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日`
    }

    // 1. 加载本年度订单金额
    async function fetchOrders() {
      try {
        const now = new Date()
        const currentYear = now.getFullYear()
        const startTime = `${currentYear}-01-01`
        const endTime = `${currentYear}-12-31`
        const res: any = await getPageList('order', { startTime, endTime, page: 1, size: 100 })
        const orders = res.data || []
        const totalAmount = orders.reduce((sum: number, item: any) => sum + (Number(item.price) || 0), 0)
        radarData.orderTotalAmount = totalAmount
        radarData.orderCount = res.total || orders.length
        radarData.orderHint = `本年度累计完成 ${radarData.orderCount} 笔成单 · 点击穿透查看`
      } catch (err) {
        console.warn('获取订单统计失败，使用备用聚合:', err)
        radarData.orderHint = '点击直达已订单列表'
      }
    }

    // 2. 加载待审批单据
    async function fetchApprovals() {
      try {
        const res: any = await getPageList('order', {
          changeStatus: 1,
          page: 1,
          size: 1
        })
        radarData.pendingApprovals = res.total || 0
      } catch (err) {
        console.warn('获取待审批订单失败:', err)
      }
    }

    // 3. 加载流转中业务
    async function fetchBusiness() {
      try {
        const [orderTypeRes, allRes]: any = await Promise.all([
          getPageList('business', { pageType: 'ORDER', page: 1, size: 1 }),
          getPageList('business', { page: 1, size: 1 })
        ])
        radarData.inProgressBusiness = orderTypeRes?.total || 0
        radarData.totalBusiness = allRes?.total || 0
        radarData.businessHint = `业务在册 ${radarData.totalBusiness} 单 · 点击穿透查看`
      } catch (err) {
        console.warn('获取流转业务失败:', err)
      }
    }

    // 4. 加载待收款项
    async function fetchPendingReceived() {
      try {
        const res: any = await getPageList('business', { pageType: 'RECEIVED', page: 1, size: 1 })
        radarData.pendingReceived = res?.total || 0
      } catch (err) {
        console.warn('获取待收款项失败:', err)
      }
    }

    // 5. 加载系统真实通知与审计日志
    async function fetchNotices() {
      noticeLoading.value = true
      try {
        const notifRes: any = await getNotificationPage({ page: 1, size: 6 })
        if (notifRes && notifRes.data && notifRes.data.length > 0) {
          noticeList.value = notifRes.data.map((item: any) => {
            let tag = '通知'
            let type = 'info'
            if (item.subject?.includes('审批') || item.type === 1) {
              tag = '审批'
              type = 'warning'
            } else if (item.subject?.includes('付款') || item.type === 2) {
              tag = '财务'
              type = 'success'
            } else if (item.subject?.includes('业务') || item.subject?.includes('订单')) {
              tag = '业务'
              type = 'info'
            }
            return {
              id: item.id,
              title: item.content || item.subject,
              time: item.createTime || '',
              tag,
              type,
              targetUrl: item.targetUrl || ''
            }
          })
        } else {
          // 回退展示用户操作审计日志
          await fetchFallbackAuditLogs()
        }
      } catch (e) {
        await fetchFallbackAuditLogs()
      } finally {
        noticeLoading.value = false
      }
    }

    async function fetchFallbackAuditLogs() {
      try {
        const opRes: any = await userOperationLogList({ page: 1, size: 6 })
        noticeList.value = (opRes.data || []).map((log: any, idx: number) => ({
          id: log.id || idx,
          title: `[${log.username || '系统用户'}] ${log.method || 'GET'} ${log.url || ''}`,
          time: log.createTime || '',
          tag: '审计',
          type: 'info',
          targetUrl: '/system/userLog/operationLog/Index'
        }))
      } catch (err) {
        console.warn('获取操作审计日志失败:', err)
      }
    }

    async function loadAllData() {
      loading.value = true
      try {
        await Promise.all([
          fetchOrders(),
          fetchApprovals(),
          fetchBusiness(),
          fetchPendingReceived(),
          fetchNotices()
        ])
      } finally {
        loading.value = false
      }
    }

    function goToRoute(path: string) {
      if (path) router.push(path)
    }

    function handleNoticeClick(item: any) {
      if (item.targetUrl) {
        router.push(item.targetUrl)
      }
    }

    onMounted(() => {
      formatDate()
      loadAllData()
    })

    return {
      currentDate,
      loading,
      noticeLoading,
      radarData,
      quickActions,
      noticeList,
      loadAllData,
      goToRoute,
      handleNoticeClick
    }
  }
})
</script>

<style scoped lang="scss">
.dashboard-container {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-radius: var(--r-md);

  .header-eyebrow {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--ink-soft);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-family: var(--font-mono);
    margin-bottom: 6px;

    .live-pulse {
      width: 7px;
      height: 7px;
      border-radius: 999px;
      background: var(--lime);
      box-shadow: 0 0 8px rgba(130, 221, 45, 0.8), 0 0 0 2px rgba(130, 221, 45, 0.2);
    }
  }

  .header-title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--ink);
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;

    .refresh-btn {
      background: var(--surface);
      border: 1px solid var(--line);
      color: var(--ink-soft);
      border-radius: var(--r-xs);
      font-size: 12px;
      transition: all 0.2s ease;

      &:hover {
        color: var(--blue);
        border-color: var(--blue);
        background: var(--pill-bg);
      }
    }

    .header-date {
      font-size: 13px;
      color: var(--ink-soft);
      font-family: var(--font-mono);
    }
  }
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
}

.dashboard-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
}

.body-panel {
  padding: 22px 24px;
  border-radius: var(--r-md);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.panel-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border-bottom: 1px solid var(--line);
  padding-bottom: 12px;

  .panel-title {
    font-size: 15px;
    font-weight: 650;
    color: var(--ink);
  }

  .panel-sub {
    font-size: 11.5px;
    color: var(--ink-faint);
  }
}

.action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.action-card {
  padding: 14px 16px;
  border-radius: var(--r-sm);
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    border-color: var(--blue);
    background: var(--pill-bg);
    transform: translateY(-2px);
    box-shadow: var(--e1);
  }

  .action-icon {
    width: 36px;
    height: 36px;
    border-radius: var(--r-xs);
    background: var(--surface-solid);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--e1);
    flex-shrink: 0;

    .iconfont {
      font-size: 18px;
    }
  }

  .action-info {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .action-title {
      font-size: 13.5px;
      font-weight: 600;
      color: var(--ink);
    }

    .action-desc {
      font-size: 11px;
      color: var(--ink-faint);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.notice-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 200px;
}

.notice-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: var(--r-xs);
  background: rgba(var(--shade), 0.02);
  transition: all 0.15s ease;

  &.is-clickable {
    cursor: pointer;

    &:hover {
      background: var(--hover-bg);
      transform: translateX(2px);
    }
  }

  .notice-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    flex-shrink: 0;

    &.info { background: var(--blue); }
    &.warning { background: #f59e0b; }
    &.success { background: var(--lime); }
  }

  .notice-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;

    .notice-title {
      font-size: 12.5px;
      color: var(--ink);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .notice-time {
      font-size: 11px;
      color: var(--ink-faint);
      font-family: var(--font-mono);
    }
  }

  .notice-tag {
    font-size: 10.5px;
    padding: 2px 7px;
    border-radius: 4px;
    background: var(--groove-bg);
    color: var(--ink-soft);
    flex-shrink: 0;
  }
}

.notice-empty {
  font-size: 12.5px;
  color: var(--ink-faint);
  text-align: center;
  padding: 40px 0;
}
</style>
